import React from 'react';
import { Box, Container, Typography, AppBar, Toolbar, Stack, ThemeProvider, CssBaseline, useTheme } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { inputsCustomizations } from '../../shared-theme/customizations/inputs';
import { dataDisplayCustomizations } from '../../shared-theme/customizations/dataDisplay';
import { feedbackCustomizations } from '../../shared-theme/customizations/feedback';
import { navigationCustomizations } from '../../shared-theme/customizations/navigation';
import { surfacesCustomizations } from '../../shared-theme/customizations/surfaces';
import { colorSchemes, typography, shadows, shape } from '../../shared-theme/themePrimitives';
import { PodcastIcon } from '@/components/icons/PodcastIcon';

function TermsContent() {
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
       {/* Simple Navbar */}
       <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(255, 255, 255, 0.8)', 
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 64 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ cursor: 'pointer' }} onClick={() => window.location.href = '/'}>
               <PodcastIcon size={32} color={theme.palette.primary.main} />
               <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.5px', color: 'text.primary' }}>
                Podstack
              </Typography>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h2" fontWeight={800} sx={{ mb: 2 }}>Terms of Service</Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 6 }}>Last updated: {new Date().toLocaleDateString()}</Typography>
        
        <Stack spacing={4}>
            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>1. Acceptance of Terms</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    By accessing and using the Podstack platform ("Service"), you accept and agree to be bound by the terms and provision of this agreement. In addition, when using these particular services, you shall be subject to any posted guidelines or rules applicable to such services.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>2. Service Description</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Podstack provides podcast research, email discovery, and outreach tools for PR professionals and founders. We reserve the right to modify, suspend, or discontinue the Service at any time, with or without notice.
                </Typography>
            </Box>

             <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>3. User Accounts & Security</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    You constitute that you are of legal age to form a binding contract. You are responsible for maintaining the confidentiality of your password and account. You are fully responsible for all activities that occur under your password or account.
                </Typography>
            </Box>

             <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>4. Intellectual Property</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    The Service and its original content, features, and functionality are and will remain the exclusive property of Podstack Inc. and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>5. Prohibited Uses</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     You agree not to use the Service:
                </Typography>
                <ul style={{ color: '#666', marginTop: 10, lineHeight: 1.7, paddingLeft: 20 }}>
                    <li>To send unsolicited mass emails (spam);</li>
                    <li>To harvset or collect email addresses or other contact information of other users from the Service or the podcasts indexed by electronic or other means for the purposes of sending unsolicited emails or other unsolicited communications;</li>
                    <li>To violate any applicable national or international law or regulation.</li>
                </ul>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>6. Subscription & Billing</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     Some parts of the Service are billed on a subscription basis ("Subscription(s)"). You will be billed in advance on a recurring and periodic basis ("Billing Cycle"). Billing cycles are set either on a monthly or annual basis, depending on the type of subscription plan you select when purchasing a Subscription.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>7. Limitation of Liability</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     In no event shall Podstack Inc., nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
                </Typography>
            </Box>

             <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>8. Governing Law</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     These Terms shall be governed and construed in accordance with the laws of Delaware, United States, without regard to its conflict of law provisions.
                </Typography>
            </Box>

             <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>9. Contact Us</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     If you have any questions about these Terms, please contact us at support@podstack.co.
                </Typography>
            </Box>
        </Stack>
      </Container>


       {/* Simple Footer */}
       <Box sx={{ py: 6, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', mt: 'auto' }}>
            <Container maxWidth="lg">
                <Typography variant="body2" color="text.disabled" align="center">
                    © {new Date().getFullYear()} Podstack Inc.
                </Typography>
            </Container>
        </Box>
    </Box>
  );
}


export default function TermsPage() {
    const lightTheme = React.useMemo(() => createTheme({
      cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
        cssVarPrefix: 'template',
      },
      colorSchemes, 
      typography,
      shadows,
      shape,
      components: {
        ...inputsCustomizations,
        ...dataDisplayCustomizations,
        ...feedbackCustomizations,
        ...navigationCustomizations,
        ...surfacesCustomizations,
      },
    }), []);
  
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <TermsContent />
      </ThemeProvider>
    );
  }
